import type { FormikErrors, FormikTouched } from "formik"
import { Formik } from "formik"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { HiCheck, HiX } from "react-icons/hi"
import * as Yup from "yup"

import { twClassNames } from "../../../../helpers"

type Props = {
  onCancel: () => void
  onSubmit: (values: { currentPassword: string; newPassword: string }) => Promise<void>
}

type ChangePasswordFormValues = {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

type PasswordStrength = {
  score: number
  checks: {
    minLength: boolean
    hasNumber: boolean
    hasSpecial: boolean
  }
}

const getPasswordStrength = (password: string): PasswordStrength => {
  const checks = {
    minLength: password.length >= 8,
    hasNumber: /\d/.test(password),
    hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
  }

  const score = Object.values(checks).filter(Boolean).length

  return { score, checks }
}

const getStrengthColor = (score: number): string => {
  if (score <= 1) return "tw-bg-danger"
  if (score === 2) return "tw-bg-amber-500"
  return "tw-bg-success"
}

const getStrengthLabel = (score: number, t: (key: string) => string): string => {
  if (score <= 1) return t("account.personal.changePassword.strength.weak")
  if (score === 2) return t("account.personal.changePassword.strength.good")
  return t("account.personal.changePassword.strength.strong")
}

export const ChangePasswordForm: React.FC<Props> = ({ onCancel, onSubmit }) => {
  const { t } = useTranslation()

  const initialValues: ChangePasswordFormValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  }

  const schema = Yup.object({
    currentPassword: Yup.string().required(t("account.personal.changePassword.validation.currentRequired")),
    newPassword: Yup.string()
      .min(8, t("common.validation.invalidPasswordMinLength", { min: 8 }))
      .required(t("account.personal.changePassword.validation.newRequired")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], t("common.validation.unmatchPassword"))
      .required(t("account.personal.changePassword.validation.confirmRequired"))
  })

  const fieldClass = (
    name: keyof ChangePasswordFormValues,
    touched: FormikTouched<ChangePasswordFormValues>,
    errors: FormikErrors<ChangePasswordFormValues>
  ) => twClassNames("tw-form-control", touched[name] && errors[name] ? "tw-is-invalid" : "")

  return (
    <Formik<ChangePasswordFormValues>
      initialValues={initialValues}
      validationSchema={schema}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        try {
          await onSubmit({
            currentPassword: values.currentPassword,
            newPassword: values.newPassword
          })
          resetForm()
          onCancel()
        } finally {
          setSubmitting(false)
        }
      }}>
      {formik => {
        const strength = useMemo(() => getPasswordStrength(formik.values.newPassword), [formik.values.newPassword])

        return (
          <form className="tw-grid tw-grid-cols-6 tw-gap-4 tw-max-w-lg" onSubmit={formik.handleSubmit}>
            <h5 className="tw-col-span-6 tw-text-lg tw-font-semibold tw-mb-2">
              {t("account.personal.changePassword.title")}
            </h5>

            {/* Current Password */}
            <div className="tw-relative tw-col-span-6">
              <label htmlFor="currentPassword" className="tw-form-label">
                {t("account.personal.changePassword.form.currentPassword")}
              </label>
              <input
                id="currentPassword"
                name="currentPassword"
                type="password"
                autoComplete="current-password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.currentPassword}
                className={fieldClass("currentPassword", formik.touched, formik.errors)}
              />
              <div className="tw-invalid-tooltip">{formik.errors.currentPassword}</div>
            </div>

            {/* New Password */}
            <div className="tw-relative tw-col-span-6">
              <label htmlFor="newPassword" className="tw-form-label">
                {t("account.personal.changePassword.form.newPassword")}
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.newPassword}
                className={fieldClass("newPassword", formik.touched, formik.errors)}
              />
              {/* Password Strength Indicator - always show when typing, replaces error tooltip */}
              {formik.values.newPassword ? (
                <div className="tw-mt-2">
                  <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                    <div className="tw-flex-1 tw-h-1.5 tw-bg-gray-200 tw-rounded-full tw-overflow-hidden">
                      <div
                        className={twClassNames(
                          "tw-h-full tw-transition-all tw-duration-300",
                          getStrengthColor(strength.score)
                        )}
                        style={{ width: `${(strength.score / 3) * 100}%` }}
                      />
                    </div>
                    <span
                      className={twClassNames(
                        "tw-text-xs tw-font-medium",
                        strength.score <= 1
                          ? "tw-text-danger"
                          : strength.score < 3
                            ? "tw-text-amber-600"
                            : "tw-text-success"
                      )}>
                      {getStrengthLabel(strength.score, t)}
                    </span>
                  </div>
                  <ul className="tw-space-y-1">
                    <li className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs">
                      {strength.checks.minLength ? (
                        <HiCheck className="tw-h-3.5 tw-w-3.5 tw-text-success" />
                      ) : (
                        <HiX className="tw-h-3.5 tw-w-3.5 tw-text-danger" />
                      )}
                      <span className={strength.checks.minLength ? "tw-text-success" : "tw-text-dark-75"}>
                        {t("account.personal.changePassword.requirements.minLength")}
                      </span>
                    </li>
                    <li className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs">
                      {strength.checks.hasNumber ? (
                        <HiCheck className="tw-h-3.5 tw-w-3.5 tw-text-success" />
                      ) : (
                        <span className="tw-h-3.5 tw-w-3.5" />
                      )}
                      <span className={strength.checks.hasNumber ? "tw-text-success" : "tw-text-dark-75"}>
                        {t("account.personal.changePassword.requirements.hasNumber")}
                      </span>
                    </li>
                    <li className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs">
                      {strength.checks.hasSpecial ? (
                        <HiCheck className="tw-h-3.5 tw-w-3.5 tw-text-success" />
                      ) : (
                        <span className="tw-h-3.5 tw-w-3.5" />
                      )}
                      <span className={strength.checks.hasSpecial ? "tw-text-success" : "tw-text-dark-75"}>
                        {t("account.personal.changePassword.requirements.hasSpecial")}
                      </span>
                    </li>
                  </ul>
                </div>
              ) : (
                <div className="tw-invalid-tooltip">{formik.errors.newPassword}</div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="tw-relative tw-col-span-6">
              <label htmlFor="confirmPassword" className="tw-form-label">
                {t("account.personal.changePassword.form.confirmPassword")}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.confirmPassword}
                className={fieldClass("confirmPassword", formik.touched, formik.errors)}
              />
              <div className="tw-invalid-tooltip">{formik.errors.confirmPassword}</div>
            </div>

            {/* Actions */}
            <div className="tw-col-span-6 tw-mt-2 tw-flex tw-gap-3">
              <button
                type="button"
                className="tw-btn tw-btn-outline"
                onClick={() => {
                  formik.resetForm()
                  onCancel()
                }}
                disabled={formik.isSubmitting}>
                {t("account.personal.button.cancel")}
              </button>
              <button type="submit" className="tw-btn tw-btn-primary" disabled={formik.isSubmitting}>
                {t("account.personal.changePassword.button.change")}
              </button>
            </div>
          </form>
        )
      }}
    </Formik>
  )
}
